import React, { useState, useEffect } from 'react';
import styles from './Blog.module.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiCalendar, FiClock, FiSearch,
  FiChevronRight, FiChevronLeft,
  FiHeart, FiMessageCircle, FiBookmark, FiEye, FiX,
  FiSend, FiCheck, FiStar
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const Blog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [savedPosts, setSavedPosts] = useState({});
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [postComments, setPostComments] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const postsPerPage = 6;

  // Blog posts data
  const [blogPosts, setBlogPosts] = useState([
    {
      id: 1,
      title: "Top 10 AI Tools for Businesses in 2025",
      excerpt: "Discover how AI can transform your business operations and drive growth. From chatbots to analytics, these tools are revolutionizing the way companies operate.",
      content: `Artificial Intelligence is no longer a futuristic concept—it's a present-day reality that's transforming how businesses operate. In 2025, the landscape of AI tools has evolved dramatically, offering unprecedented capabilities to companies of all sizes.

      Here are the top 10 AI tools that every business should consider:

      1. ChatGPT Enterprise: Advanced language model for customer service, content creation, and internal communications. With enhanced security features and customization options, it's become the go-to choice for enterprises.

      2. Jasper AI: Specialized in marketing content generation, helping teams create compelling copy, blog posts, and social media content in minutes rather than hours.

      3. Midjourney 6.0: The latest version of this image generation tool produces photorealistic visuals that are transforming how businesses approach design and branding.

      4. Salesforce Einstein: Integrated AI that provides predictive analytics, customer insights, and automated workflows directly within the Salesforce ecosystem.

      5. TensorFlow Enterprise: Google's machine learning platform with enterprise-grade support, security, and scalability for building custom AI solutions.

      6. IBM Watson Assistant: Advanced conversational AI platform that powers customer service chatbots with natural language understanding and seamless integration.

      7. DataRobot: Automated machine learning platform that enables businesses to build and deploy predictive models without extensive data science expertise.

      8. Grammarly Business: AI-powered writing assistant that ensures professional communication across all company channels.

      9. Luminance: AI for legal document analysis and contract review, saving countless hours in due diligence processes.

      10. C3.ai: Enterprise AI platform that provides pre-built AI applications for industries like energy, manufacturing, and financial services.

      These tools are revolutionizing business operations by automating routine tasks, providing deep insights, and enabling new levels of creativity and efficiency. The key is to identify which tools align with your specific business needs and to implement them strategically.

      As we move through 2025, we can expect even more sophisticated AI tools to emerge, further blurring the line between human and machine capabilities. Businesses that embrace these technologies will find themselves at a significant competitive advantage.`,
      date: "March 15, 2025",
      category: "AI Solutions",
      readTime: "5 min read",
      author: "John Doe",
      authorRole: "AI Specialist",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "top-10-ai-tools",
      views: 1234,
      likes: 89,
      comments: 23,
      tags: ["AI", "Business Tools", "Technology"]
    },
    {
      id: 2,
      title: "How Startups Build MVP Products Successfully",
      excerpt: "A step-by-step guide to building and launching your minimum viable product. Learn the essential steps to validate your idea and attract early adopters.",
      content: `Building a Minimum Viable Product (MVP) is the most critical phase in a startup's journey. It's the bridge between your idea and your first real users. Here's a comprehensive guide to building your MVP successfully.

      Step 1: Define Your Core Value Proposition
      Before writing a single line of code, you must identify the core problem your product solves. What is the one thing your product does better than anyone else? This becomes your MVP's foundation.

      Step 2: Identify Key Features
      List all the features you envision for your product, then ruthlessly prioritize. Your MVP should include only the essential features needed to solve the core problem. Use the MoSCoW method (Must have, Should have, Could have, Won't have) to prioritize.

      Step 3: Create User Personas
      Understand who your early adopters are. Create detailed personas including their demographics, pain points, and goals. This will guide your design and development decisions.

      Step 4: Design the User Flow
      Map out how users will interact with your MVP. Focus on creating the simplest possible path to value. Every additional click is a potential drop-off point.

      Step 5: Choose Your Tech Stack Wisely
      Select technologies that allow for rapid development and easy iteration. Popular choices include React for frontend, Node.js for backend, and MongoDB for database. Consider using no-code tools for initial validation.

      Step 6: Build with Speed
      Set a tight timeline (typically 2-3 months) to launch your MVP. Use agile methodologies and focus on delivering working software quickly. Remember, perfection is the enemy of done.

      Step 7: Test with Real Users
      Before launch, get your MVP in front of real users. Conduct usability tests and gather feedback. Watch how users interact with your product without guidance.

      Step 8: Launch and Measure
      Launch to a small group of early adopters. Set up analytics to track user behavior. Key metrics to watch: user engagement, retention rate, and feature adoption.

      Step 9: Iterate Based on Feedback
      Use the feedback and data to improve your product. Be prepared to pivot if necessary. The goal is to learn what works and what doesn't as quickly as possible.

      Common MVP Mistakes to Avoid:
      - Building too many features
      - Ignoring user feedback
      - Over-engineering the solution
      - Delaying launch for perfection
      - Not validating the market need

      Remember, your MVP is just the beginning. It's a learning tool that helps you understand your users and market. The insights you gain will be invaluable as you build towards your full vision.`,
      date: "March 10, 2025",
      category: "Startups",
      readTime: "7 min read",
      author: "Jane Smith",
      authorRole: "Product Strategist",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "startup-mvp-guide",
      views: 2341,
      likes: 156,
      comments: 34,
      tags: ["Startups", "MVP", "Product Development"]
    },
    {
      id: 3,
      title: "Why Every Business Needs Automation in 2025",
      excerpt: "Learn how automation can save time, reduce costs, and improve efficiency. Discover the key areas where automation can make the biggest impact.",
      content: `In 2025, automation has transitioned from a competitive advantage to a business necessity. Companies that fail to automate critical processes find themselves struggling to compete with more efficient rivals. Here's why every business needs automation now.

      The Case for Automation

      1. Time Savings
      Automation frees your team from repetitive, manual tasks. Consider this: the average employee spends 2.5 hours per day on tasks that could be automated. That's over 600 hours per year per employee that could be redirected to high-value activities.

      2. Cost Reduction
      Automated processes are significantly cheaper than manual ones. A task that costs $5 when done manually might cost just $0.50 when automated. Over time, these savings add up dramatically.

      3. Error Elimination
      Humans make mistakes, especially on repetitive tasks. Automation ensures consistent, error-free execution every time. This is particularly crucial in areas like data entry, calculations, and compliance reporting.

      4. 24/7 Operations
      Unlike human workers, automated systems work around the clock. Your business can continue operating—processing orders, responding to customers, analyzing data—even while your team sleeps.

      5. Scalability
      When your business grows, manual processes become bottlenecks. Automated systems scale effortlessly, handling increased volume without additional headcount.

      Key Areas for Automation

      Customer Service
      AI-powered chatbots can handle up to 80% of routine customer inquiries, providing instant responses 24/7. Human agents are then free to focus on complex issues that require empathy and judgment.

      Marketing
      Automate email campaigns, social media posting, lead scoring, and ad optimization. Personalize customer communications at scale based on behavior and preferences.

      Sales
      Automate lead qualification, follow-up emails, appointment scheduling, and CRM updates. Sales teams can focus on building relationships and closing deals.

      Finance and Accounting
      Automate invoicing, expense tracking, reconciliation, and financial reporting. Reduce errors and get real-time visibility into your financial health.

      Human Resources
      Automate candidate screening, onboarding paperwork, time-off requests, and performance reviews. HR teams can focus on culture and employee development.

      Operations
      Automate inventory management, order processing, supply chain monitoring, and quality control. Improve efficiency and reduce waste.

      Getting Started with Automation

      1. Identify repetitive tasks: Look for tasks that are time-consuming, error-prone, and rule-based.
      2. Measure current costs: Calculate how much time and money these tasks currently consume.
      3. Research solutions: Find automation tools that address your specific needs.
      4. Start small: Pilot automation in one area before expanding.
      5. Train your team: Ensure employees understand how to work alongside automated systems.
      6. Measure results: Track improvements in efficiency, accuracy, and cost savings.

      The businesses that thrive in 2025 will be those that embrace automation strategically. Start identifying opportunities today, and position your company for success in the automated future.`,
      date: "March 5, 2025",
      category: "Automation",
      readTime: "6 min read",
      author: "Mike Johnson",
      authorRole: "Automation Expert",
      image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "business-automation",
      views: 1876,
      likes: 112,
      comments: 28,
      tags: ["Automation", "Efficiency", "Business Growth"]
    },
    {
      id: 4,
      title: "The Future of Web Development: Trends to Watch",
      excerpt: "Explore the latest trends in web development and how they're shaping the digital landscape. From AI-powered interfaces to progressive web apps.",
      content: `The web development landscape is evolving at breakneck speed. What was cutting-edge last year is already becoming standard practice. Here are the trends that will define web development in 2025 and beyond.

      1. AI-Powered Development
      AI is no longer just a feature of websites—it's becoming integral to how we build them. Tools like GitHub Copilot are transforming coding, while AI-driven design tools are creating interfaces that adapt to user behavior in real-time.

      2. WebAssembly (Wasm)
      WebAssembly is revolutionizing what's possible in the browser. It allows developers to run high-performance code written in languages like C++, Rust, and Go directly in the browser, opening up possibilities for complex applications like video editing, 3D gaming, and scientific computing.

      3. Progressive Web Apps (PWAs)
      PWAs continue to blur the line between websites and native apps. With offline functionality, push notifications, and home screen installation, PWAs offer app-like experiences without the friction of app store downloads.

      4. Serverless Architecture
      Serverless computing allows developers to focus on code without managing infrastructure. This trend is accelerating, with more companies adopting function-as-a-service platforms for scalability and cost efficiency.

      5. JAMstack Evolution
      The JAMstack (JavaScript, APIs, Markup) approach continues to mature. Modern static site generators and headless CMS platforms make it easier than ever to build fast, secure, and scalable websites.

      6. Motion UI
      User interfaces are becoming more dynamic and engaging. Micro-interactions, animated transitions, and scroll-triggered effects are no longer just nice-to-have—they're expected by users.

      7. Voice-Activated Interfaces
      With the proliferation of smart speakers and voice assistants, voice-activated web interfaces are becoming more common. Developers are learning to design for conversation, not just visual interaction.

      8. Cybersecurity by Default
      As threats evolve, security is shifting from an afterthought to a built-in feature. Expect to see more emphasis on secure coding practices, automated security testing, and zero-trust architectures.

      9. Edge Computing
      Processing data closer to users—at the edge of the network—is reducing latency and improving performance. CDNs are evolving into full-fledged compute platforms.

      10. Sustainability
      Green coding is gaining attention. Developers are optimizing code and infrastructure to reduce energy consumption and carbon footprint.

      Skills for the Future Web Developer

      To stay relevant, web developers should focus on:
      - Mastering modern JavaScript frameworks (React, Vue, Svelte)
      - Understanding cloud architecture and serverless concepts
      - Learning performance optimization techniques
      - Developing AI/ML literacy
      - Building accessibility-first interfaces
      - Embracing continuous learning

      The future of web development is bright and full of opportunity. Developers who stay curious and adaptable will thrive in this rapidly evolving landscape.`,
      date: "February 28, 2025",
      category: "Web Development",
      readTime: "8 min read",
      author: "Sarah Williams",
      authorRole: "Lead Developer",
      image: "https://images.unsplash.com/photo-1627398242454-45a1465c2479?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "web-development-trends",
      views: 1567,
      likes: 134,
      comments: 31,
      tags: ["Web Development", "Technology", "Trends"]
    },
    {
      id: 5,
      title: "Mobile App Development: Native vs Cross-Platform",
      excerpt: "A comprehensive comparison between native and cross-platform development approaches. Which one is right for your project?",
      content: `One of the most persistent debates in mobile development is whether to build native or cross-platform apps. Both approaches have passionate advocates, and the right choice depends on your specific project requirements. Here's a comprehensive comparison to help you decide.

      Native Development

      Native apps are built specifically for one platform—Swift/Objective-C for iOS, Kotlin/Java for Android.

      Pros:
      - Performance: Native apps offer the best possible performance, with direct access to device hardware and platform-specific optimizations.
      - User Experience: They provide the most authentic platform experience, following platform-specific design guidelines (Human Interface Guidelines for iOS, Material Design for Android).
      - Access to Latest Features: Native development gives you immediate access to new platform features as soon as they're released.
      - Better Testing Tools: Each platform has mature testing frameworks and tools.

      Cons:
      - Cost: You need separate teams (or developers with expertise in both platforms), effectively doubling development costs.
      - Time to Market: Building two separate apps takes significantly longer.
      - Code Duplication: Business logic must be implemented twice, increasing the chance of inconsistencies.

      Cross-Platform Development

      Cross-platform frameworks like React Native, Flutter, and Xamarin allow you to build one codebase that runs on both platforms.

      Pros:
      - Cost-Effective: One team can build for both platforms, significantly reducing development costs.
      - Faster Development: Write once, deploy everywhere—this dramatically speeds up time to market.
      - Consistent Business Logic: Core functionality is implemented once, ensuring consistency across platforms.
      - Easier Maintenance: Updates and bug fixes are applied once for both platforms.

      Cons:
      - Performance: While frameworks have improved dramatically, there's still a performance gap, especially for graphics-intensive apps.
      - Platform Limitations: You may encounter limitations when trying to access platform-specific features or when platform updates introduce breaking changes.
      - Larger App Size: Cross-platform apps tend to be larger than their native counterparts.
      - Delayed Feature Access: Access to new platform features often requires waiting for framework updates.

      When to Choose Native

      Choose native development when:
      - You need maximum performance (gaming, AR/VR, real-time processing)
      - Your app requires deep integration with platform-specific features
      - You have the budget for separate teams
      - User experience is critical and must feel completely native
      - You're building for a single platform initially

      When to Choose Cross-Platform

      Choose cross-platform when:
      - You have budget constraints
      - Speed to market is critical
      - Your app doesn't require intensive graphics or animations
      - You want to validate your idea with an MVP on both platforms
      - You have a small team with limited resources

      Popular Frameworks Compared

      React Native: Great for teams with React web experience, large ecosystem, mature but can have bridging complexity.

      Flutter: Excellent performance, beautiful UI with custom widgets, growing rapidly, uses Dart language.

      Xamarin: Good for .NET/C# teams, strong integration with Microsoft ecosystem, can be more complex.

      Ionic: Web-based (HTML/CSS/JavaScript), good for simple apps, not ideal for complex functionality.

      The choice between native and cross-platform isn't binary. Many successful apps use a hybrid approach—cross-platform for most features, native for performance-critical components. Consider your specific needs, resources, and long-term goals when making this important decision.`,
      date: "February 20, 2025",
      category: "Mobile Development",
      readTime: "6 min read",
      author: "David Brown",
      authorRole: "Mobile Architect",
      image: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "native-vs-cross-platform",
      views: 1432,
      likes: 98,
      comments: 19,
      tags: ["Mobile Development", "Technology", "Comparison"]
    },
    {
      id: 6,
      title: "Enterprise Software: Digital Transformation Guide",
      excerpt: "How modern enterprises are leveraging software to transform their operations and stay competitive in the digital age.",
      content: `Digital transformation is no longer optional for enterprises—it's essential for survival. Companies that successfully leverage modern software to transform their operations gain significant competitive advantages. Here's your comprehensive guide to enterprise digital transformation.

      What is Digital Transformation?

      Digital transformation is the integration of digital technology into all areas of a business, fundamentally changing how you operate and deliver value to customers. It's not just about adopting new technology—it's about reimagining business processes, culture, and customer experiences.

      The Digital Transformation Imperative

      1. Changing Customer Expectations
      Customers expect seamless, personalized, omnichannel experiences. They compare your digital experience to the best they've encountered anywhere—not just in your industry.

      2. Competitive Pressure
      Agile startups and tech-native competitors are disrupting established industries. Traditional enterprises must transform or risk becoming obsolete.

      3. Operational Efficiency
      Digital tools can dramatically reduce costs, eliminate waste, and improve productivity. Processes that took days can be completed in hours or minutes.

      4. Data-Driven Decision Making
      Modern enterprises generate massive amounts of data. Digital transformation enables you to collect, analyze, and act on this data in real-time.

      5. Talent Attraction and Retention
      Top talent expects to work with modern tools and technologies. Outdated systems make it difficult to attract and retain the best employees.

      Key Areas of Digital Transformation

      1. Customer Experience
      - Omnichannel engagement platforms
      - Personalization engines
      - Customer journey analytics
      - Self-service portals
      - AI-powered customer support

      2. Operational Processes
      - Robotic process automation (RPA)
      - Workflow automation
      - Supply chain digitization
      - Inventory management systems
      - Quality control automation

      3. Business Models
      - Product-as-a-service offerings
      - Subscription-based models
      - Platform-based ecosystems
      - Data monetization
      - Digital marketplaces

      4. Employee Empowerment
      - Collaboration tools
      - Mobile workforce solutions
      - Knowledge management systems
      - Digital training platforms
      - Performance analytics

      5. Data and Analytics
      - Real-time dashboards
      - Predictive analytics
      - Business intelligence platforms
      - Data lakes and warehouses
      - Machine learning models

      The Digital Transformation Roadmap

      Phase 1: Assessment
      - Audit current systems and processes
      - Identify pain points and opportunities
      - Benchmark against competitors
      - Define success metrics

      Phase 2: Strategy
      - Set clear goals and objectives
      - Prioritize initiatives
      - Develop business cases
      - Create implementation roadmap
      - Secure executive buy-in

      Phase 3: Foundation
      - Upgrade infrastructure
      - Implement cloud strategy
      - Establish data governance
      - Build security framework
      - Develop API strategy

      Phase 4: Implementation
      - Launch pilot projects
      - Agile development cycles
      - Change management
      - Training and support
      - Continuous feedback loops

      Phase 5: Optimization
      - Measure results against metrics
      - Iterate and improve
      - Scale successful initiatives
      - Foster innovation culture
      - Stay current with emerging tech

      Common Pitfalls to Avoid

      - Technology-first approach: Transformation should be driven by business strategy, not technology.
      - Lack of executive sponsorship: Without strong leadership support, initiatives will stall.
      - Siloed implementation: Digital transformation must be enterprise-wide, not departmental.
      - Ignoring culture: Technology changes are meaningless without cultural change.
      - Underestimating change management: People are the hardest part of transformation.

      Measuring Success

      Key metrics to track:
      - Customer satisfaction scores
      - Operational efficiency gains
      - Revenue growth from digital channels
      - Time-to-market improvements
      - Employee productivity metrics
      - Innovation velocity

      Digital transformation is a journey, not a destination. The most successful enterprises approach it as an ongoing process of continuous improvement and adaptation. Start where you are, use what you have, and do what you can—but start today.`,
      date: "February 15, 2025",
      category: "Enterprise Solutions",
      readTime: "9 min read",
      author: "Emily Davis",
      authorRole: "Enterprise Architect",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "enterprise-digital-transformation",
      views: 1123,
      likes: 76,
      comments: 15,
      tags: ["Enterprise", "Digital Transformation", "Business"]
    },
    {
      id: 7,
      title: "Cybersecurity Best Practices for 2025",
      excerpt: "Essential cybersecurity measures every business should implement to protect their digital assets and customer data.",
      content: `As cyber threats become more sophisticated, businesses must evolve their security practices. Here are the essential cybersecurity measures every organization should implement in 2025.

      The Evolving Threat Landscape

      Cyber threats in 2025 are more sophisticated than ever. Ransomware attacks target critical infrastructure, AI-powered phishing campaigns are nearly impossible to detect, and supply chain attacks compromise trusted software vendors. The stakes have never been higher.

      Zero Trust Architecture

      The old security model—trust inside, distrust outside—is obsolete. Zero Trust assumes that threats exist both inside and outside the network. Key principles include:

      - Verify explicitly: Always authenticate and authorize based on all available data points.
      - Use least privilege access: Limit user access to only what's needed for their role.
      - Assume breach: Design systems to minimize blast radius and detect breaches quickly.

      Multi-Factor Authentication (MFA)

      Passwords alone are no longer sufficient. Implement MFA everywhere possible:
      - Require MFA for all system access
      - Use phishing-resistant authentication methods
      - Consider biometric authentication for sensitive systems
      - Implement adaptive MFA based on risk factors

      Regular Security Training

      Humans remain the weakest link in security. Implement comprehensive training:
      - Monthly security awareness training
      - Regular phishing simulations
      - Clear reporting procedures for suspicious activity
      - Gamified learning to increase engagement

      Patch Management

      Unpatched vulnerabilities are a primary attack vector. Establish a robust patch management process:
      - Automated patch deployment where possible
      - Risk-based prioritization of patches
      - Testing patches before deployment
      - Emergency procedures for critical vulnerabilities

      Data Encryption

      Protect data at rest and in transit:
      - Encrypt all sensitive data
      - Use strong encryption standards (AES-256)
      - Implement proper key management
      - Encrypt backups and archives

      Incident Response Plan

      Every organization needs a well-documented incident response plan:
      - Clear roles and responsibilities
      - Communication protocols
      - Step-by-step response procedures
      - Regular tabletop exercises
      - Post-incident review processes

      Cloud Security

      As more workloads move to the cloud, specific considerations include:
      - Proper configuration of cloud services
      - Identity and access management
      - Data loss prevention
      - Cloud security posture management
      - Shared responsibility model understanding

      Endpoint Protection

      With remote work here to stay, endpoint security is critical:
      - Next-generation antivirus
      - Endpoint detection and response (EDR)
      - Mobile device management
      - Bring your own device (BYOD) policies
      - Regular endpoint compliance checks

      Third-Party Risk Management

      Your security is only as strong as your weakest vendor:
      - Vendor security assessments
      - Contractual security requirements
      - Regular vendor audits
      - Supply chain security reviews
      - Access revocation when relationships end

      Compliance and Regulations

      Stay current with evolving regulations:
      - GDPR, CCPA, and other privacy laws
      - Industry-specific regulations (HIPAA, PCI-DSS)
      - Emerging AI regulations
      - Breach notification requirements
      - Regular compliance audits

      Emerging Technologies

      Leverage new technologies to enhance security:
      - AI-powered threat detection
      - Security orchestration and automation
      - Deception technology
      - Extended detection and response (XDR)
      - Blockchain for integrity verification

      Budgeting for Security

      Security requires investment:
      - Allocate 10-15% of IT budget to security
      - Invest in both technology and people
      - Consider cyber insurance
      - Plan for ongoing training and tools
      - Build security into projects from the start

      Remember: Security is a journey, not a destination. The threat landscape will continue to evolve, and your security practices must evolve with it. Stay informed, stay vigilant, and make security a fundamental part of your business culture.`,
      date: "February 10, 2025",
      category: "Security",
      readTime: "7 min read",
      author: "Tom Wilson",
      authorRole: "Security Expert",
      image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "cybersecurity-best-practices",
      views: 1987,
      likes: 145,
      comments: 37,
      tags: ["Security", "Business", "Technology"]
    },
    {
      id: 8,
      title: "Cloud Computing: Migration Strategies",
      excerpt: "A complete guide to migrating your business to the cloud. Learn about different strategies and best practices for a smooth transition.",
      content: `Cloud migration is one of the most significant technology initiatives a business can undertake. Done right, it can unlock unprecedented agility, scalability, and innovation. Done wrong, it can lead to cost overruns, security issues, and operational disruptions. Here's your complete guide to successful cloud migration.

      Why Move to the Cloud?

      Before diving into how, it's important to understand why organizations are migrating:

      - Scalability: Instantly scale resources up or down based on demand
      - Cost Efficiency: Pay only for what you use, reduce capital expenditure
      - Agility: Launch new products and features faster
      - Innovation: Access cutting-edge technologies (AI, serverless, etc.)
      - Reliability: Benefit from enterprise-grade infrastructure
      - Global Reach: Deploy applications closer to users worldwide

      Cloud Migration Strategies (The 6 Rs)

      1. Rehost (Lift and Shift)
      Move applications to the cloud without modification.
      - Best for: Quick wins, immediate cost savings, applications nearing end-of-life
      - Pros: Fastest migration path, minimal risk
      - Cons: Doesn't leverage cloud-native benefits

      2. Replatform (Lift, Tinker and Shift)
      Make minor optimizations during migration (e.g., moving to a managed database).
      - Best for: Applications that can benefit from cloud services without full rearchitecture
      - Pros: Better performance/cost balance than rehost
      - Cons: More complex than simple lift-and-shift

      3. Refactor / Re-architect
      Rebuild applications to be cloud-native.
      - Best for: Applications needing maximum agility and scalability
      - Pros: Full cloud benefits, future-proof
      - Cons: Most time-consuming and expensive

      4. Repurchase
      Replace with SaaS alternatives.
      - Best for: Commodity applications (CRM, HR, email)
      - Pros: Offload maintenance, access best-in-class features
      - Cons: Less customization, data migration challenges

      5. Retire
      Decommission applications that are no longer needed.
      - Best for: Applications identified as redundant during discovery
      - Pros: Reduce costs, simplify landscape
      - Cons: Requires thorough application portfolio analysis

      6. Retain
      Keep applications on-premises for now.
      - Best for: Highly regulated applications, dependencies that prevent migration
      - Pros: Avoid unnecessary risk
      - Cons: Miss cloud benefits, maintain legacy infrastructure

      The Migration Process

      Phase 1: Discovery and Assessment
      - Inventory all applications and dependencies
      - Assess technical feasibility
      - Analyze costs (current vs. cloud)
      - Identify compliance requirements
      - Prioritize applications for migration

      Phase 2: Planning
      - Select migration strategy for each application
      - Design target architecture
      - Create detailed migration timeline
      - Plan for data migration
      - Develop testing strategy
      - Prepare contingency plans

      Phase 3: Proof of Concept
      - Migrate a low-risk application first
      - Validate processes and tools
      - Measure performance and costs
      - Train the team
      - Refine approach based on learnings

      Phase 4: Migration
      - Execute migrations in waves
      - Follow the "pilot, parallel run, cutover" pattern
      - Maintain clear communication
      - Document everything
      - Celebrate wins

      Phase 5: Optimization
      - Review and optimize costs
      - Implement monitoring and alerting
      - Establish FinOps practices
      - Continue modernization
      - Share lessons learned

      Common Challenges and Solutions

      1. Skill Gaps
      Cloud requires new skills. Invest in training, hire experienced architects, and consider managed service providers.

      2. Security Concerns
      Cloud security is a shared responsibility. Understand the model, implement proper controls, and use cloud-native security tools.

      3. Cost Management
      Cloud costs can spiral without governance. Implement tagging, set budgets, use reserved instances, and monitor usage.

      4. Compliance
      Ensure cloud providers meet your compliance needs. Review certifications, implement controls, and maintain audit trails.

      5. Legacy Dependencies
      Some applications have complex dependencies. Map dependencies thoroughly and consider phased migrations.

      6. Data Migration
      Moving large datasets can be challenging. Use dedicated migration services, plan for downtime, and verify data integrity.

      Tools and Services

      Major cloud providers offer migration tools:
      - AWS: AWS Migration Hub, AWS Application Migration Service
      - Azure: Azure Migrate
      - Google Cloud: Migrate for Compute Engine

      Third-party tools can also help with discovery, planning, and execution.

      Measuring Success

      Key metrics to track:
      - Migration velocity (applications per month)
      - Cost savings vs. on-premises
      - Performance improvements
      - Security posture
      - User satisfaction
      - Time to market for new features

      Cloud migration is a journey that typically takes 12-24 months for enterprises. Start with a clear strategy, build momentum with early wins, and continuously optimize as you go. The result will be a more agile, innovative, and competitive business.`,
      date: "February 5, 2025",
      category: "Cloud Computing",
      readTime: "8 min read",
      author: "Lisa Chen",
      authorRole: "Cloud Architect",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "cloud-migration-strategies",
      views: 1654,
      likes: 112,
      comments: 24,
      tags: ["Cloud", "Infrastructure", "Migration"]
    },
    {
      id: 9,
      title: "UX Design Principles for Digital Products",
      excerpt: "Discover the fundamental principles of user experience design and how they can make your digital products more engaging and effective.",
      content: `User Experience (UX) design is the difference between products people tolerate and products people love. Great UX isn't accidental—it's the result of applying fundamental principles consistently. Here are the essential UX principles every digital product should follow.

      What is UX Design?

      UX design encompasses all aspects of a user's interaction with a company, its services, and its products. It's about creating seamless, meaningful, and delightful experiences that meet user needs while achieving business goals.

      Core UX Principles

      1. User-Centered Design
      Start with users, not technology. Understand their needs, goals, and pain points through research. Every design decision should be validated with real users.

      2. Consistency
      Users learn through repetition. Consistent patterns, terminology, and behaviors reduce cognitive load and make products easier to use. Follow platform conventions and maintain internal consistency.

      3. Hierarchy
      Guide users' attention to what matters most. Use visual hierarchy (size, color, contrast, spacing) to create clear focal points. Important elements should stand out; less important ones should recede.

      4. Affordance
      Design elements should suggest how they're used. Buttons should look clickable, sliders should look draggable, and inputs should look typeable. When affordances are clear, users don't need instructions.

      5. Feedback
      Every user action should have a response. Show loading states, success messages, error alerts, and confirmations. Users should always know what's happening and whether their actions succeeded.

      6. Simplicity
      Remove anything that doesn't serve user needs. Every additional feature, option, or piece of content adds cognitive load. As Antoine de Saint-Exupéry said, "Perfection is achieved not when there is nothing more to add, but when there is nothing left to take away."

      7. Accessibility
      Design for everyone, including people with disabilities. Follow WCAG guidelines, ensure sufficient color contrast, support keyboard navigation, and work with screen readers. Accessible design is better design for everyone.

      8. Error Prevention and Recovery
      Prevent errors through clear constraints and confirmation. When errors do occur, provide clear, actionable messages that help users recover quickly. Don't punish users for mistakes—help them fix them.

      9. Familiarity
      Leverage existing mental models. Users bring expectations from other products and the physical world. Meet these expectations unless you have a compelling reason to do otherwise.

      10. Flexibility
      Accommodate different user skill levels and preferences. Provide shortcuts for power users while maintaining simplicity for beginners. Allow customization where it adds value.

      The UX Design Process

      1. Research
      - User interviews
      - Surveys and questionnaires
      - Competitive analysis
      - Analytics review
      - Stakeholder interviews

      2. Define
      - User personas
      - Problem statements
      - User journeys
      - Requirements gathering
      - Success metrics

      3. Ideate
      - Brainstorming sessions
      - Sketching
      - Crazy 8s
      - Storyboarding
      - Design studios

      4. Prototype
      - Low-fidelity wireframes
      - Interactive prototypes
      - Design systems
      - High-fidelity mockups
      - Design specifications

      5. Test
      - Usability testing
      - A/B testing
      - Heatmaps and analytics
      - Expert reviews
      - Feedback collection

      6. Iterate
      - Refine based on feedback
      - Continuous improvement
      - Measure and optimize
      - Document learnings

      Common UX Mistakes to Avoid

      - Designing for yourself, not your users
      - Ignoring mobile users
      - Overloading interfaces with features
      - Poor information architecture
      - Inconsistent design patterns
      - Neglecting micro-interactions
      - Skipping user testing
      - Treating accessibility as optional

      UX Metrics to Track

      - Task success rate
      - Time on task
      - Error rate
      - User satisfaction (SUS, NPS)
      - Retention and engagement
      - Conversion rates

      The Business Case for UX

      Good UX isn't just nice to have—it drives business results:
      - Increased conversion: Clear, easy-to-use interfaces convert better
      - Reduced support costs: Intuitive products need less customer support
      - Higher retention: Users stick with products they enjoy using
      - Competitive advantage: Great UX differentiates you from competitors
      - Brand perception: UX shapes how users perceive your brand

      Great UX design requires empathy, iteration, and a relentless focus on user needs. By applying these principles consistently, you can create products that users don't just use—they love.`,
      date: "January 28, 2025",
      category: "Design",
      readTime: "6 min read",
      author: "Anna Garcia",
      authorRole: "UX Designer",
      image: "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      slug: "ux-design-principles",
      views: 1345,
      likes: 167,
      comments: 29,
      tags: ["Design", "UX", "Product Design"]
    }
  ]);

  // Categories for filtering
  const categories = [
    { id: 'all', name: 'All Posts', count: blogPosts.length },
    { id: 'AI Solutions', name: 'AI Solutions', count: blogPosts.filter(post => post.category === 'AI Solutions').length },
    { id: 'Startups', name: 'Startups', count: blogPosts.filter(post => post.category === 'Startups').length },
    { id: 'Automation', name: 'Automation', count: blogPosts.filter(post => post.category === 'Automation').length },
    { id: 'Web Development', name: 'Web Development', count: blogPosts.filter(post => post.category === 'Web Development').length },
    { id: 'Mobile Development', name: 'Mobile Development', count: blogPosts.filter(post => post.category === 'Mobile Development').length },
    { id: 'Enterprise Solutions', name: 'Enterprise Solutions', count: blogPosts.filter(post => post.category === 'Enterprise Solutions').length },
    { id: 'Security', name: 'Security', count: blogPosts.filter(post => post.category === 'Security').length },
    { id: 'Cloud Computing', name: 'Cloud Computing', count: blogPosts.filter(post => post.category === 'Cloud Computing').length },
    { id: 'Design', name: 'Design', count: blogPosts.filter(post => post.category === 'Design').length }
  ];

  // Filter posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Handle post click
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
    setShowCommentBox(false);
    setCommentText('');
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPost(null);
    document.body.style.overflow = 'unset';
    setShowCommentBox(false);
    setCommentText('');
  };

  // Handle like
  const handleLike = (postId) => {
    setLikedPosts(prev => {
      const isLiked = prev[postId];
      // Update post likes count
      setBlogPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, likes: post.likes + (isLiked ? -1 : 1) }
            : post
        )
      );
      // Show notification
      setNotificationMessage(isLiked ? 'Like removed' : 'Post liked!');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 2000);
      // Toggle like state
      return { ...prev, [postId]: !isLiked };
    });
  };

  // Handle save
  const handleSave = (postId) => {
    setSavedPosts(prev => {
      const isSaved = prev[postId];
      // Show notification with animation
      setNotificationMessage(isSaved ? 'Post removed from saved' : 'Post saved successfully!');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 2000);
      // Toggle save state
      return { ...prev, [postId]: !isSaved };
    });
  };

  // Handle comment submit
  const handleCommentSubmit = (postId) => {
    if (commentText.trim()) {
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), {
          id: Date.now(),
          text: commentText,
          author: 'You',
          date: new Date().toLocaleDateString(),
          likes: 0
        }]
      }));
      
      // Update comment count
      setBlogPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, comments: post.comments + 1 }
            : post
        )
      );
      
      setCommentText('');
      setShowCommentBox(false);
      setNotificationMessage('Comment added successfully!');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 2000);
    }
  };

  // Handle category change
  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <motion.div 
      className={styles.blogPage}
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* Notification Toast */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              background: 'linear-gradient(135deg, #B08B35, #AF8A32)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '50px',
              boxShadow: '0 10px 30px rgba(176, 139, 53, 0.3)',
              zIndex: 1000001,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontWeight: 500
            }}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
          >
            <FiCheck size={20} />
            {notificationMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div className={styles.heroContent} variants={fadeInUp}>
            <h1>Our Blog</h1>
            <p>Insights, trends, and expert advice on technology, innovation, and digital transformation</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.contentGrid}>
            {/* Left Sidebar */}
            <motion.aside className={styles.sidebar} variants={fadeInUp}>
              {/* Search */}
              <div className={styles.searchWidget}>
                <h3>Search</h3>
                <form onSubmit={handleSearch} className={styles.searchForm}>
                  <input 
                    type="text" 
                    placeholder="Search articles..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button type="submit">
                    <FiSearch />
                  </button>
                </form>
              </div>

              {/* Categories */}
              <div className={styles.categoriesWidget}>
                <h3>Categories</h3>
                <ul className={styles.categoryList}>
                  {categories.map(category => (
                    <li key={category.id}>
                      <button
                        className={`${styles.categoryButton} ${selectedCategory === category.id ? styles.active : ''}`}
                        onClick={() => handleCategoryChange(category.id)}
                      >
                        <span>{category.name}</span>
                        <span className={styles.categoryCount}>{category.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Popular Tags */}
              <div className={styles.tagsWidget}>
                <h3>Popular Tags</h3>
                <div className={styles.tagsCloud}>
                  {['AI', 'Startups', 'Automation', 'Web Dev', 'Mobile', 'Cloud', 'Security', 'Design', 'MVP', 'Digital Transformation'].map(tag => (
                    <button
                      key={tag}
                      className={styles.tagButton}
                      onClick={() => setSearchTerm(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </motion.aside>

            {/* Main Posts Grid */}
            <main className={styles.postsGrid}>
              {/* Results count */}
              <div className={styles.resultsCount}>
                Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, filteredPosts.length)} of {filteredPosts.length} articles
              </div>

              {/* Posts */}
              <motion.div 
                className={styles.postsList}
                variants={staggerContainer}
              >
                {currentPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className={styles.postCard}
                    variants={fadeInUp}
                    whileHover={{ y: -5 }}
                    onClick={() => handlePostClick(post)}
                  >
                    <div className={styles.postImage}>
                      <img src={post.image} alt={post.title} />
                      <span className={styles.postCategory}>{post.category}</span>
                    </div>
                    <div className={styles.postContent}>
                      <div className={styles.postMeta}>
                        <span className={styles.postDate}>
                          <FiCalendar /> {post.date}
                        </span>
                        <span className={styles.postReadTime}>
                          <FiClock /> {post.readTime}
                        </span>
                      </div>
                      <h2 className={styles.postTitle}>{post.title}</h2>
                      <p className={styles.postExcerpt}>{post.excerpt}</p>
                      <div className={styles.postFooter}>
                        <div className={styles.postAuthor}>
                          <div className={styles.authorInitials}>
                            {post.author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <span className={styles.authorName}>{post.author}</span>
                            <span className={styles.authorRole}>{post.authorRole}</span>
                          </div>
                        </div>
                        <div className={styles.postStats}>
                          <span><FiEye /> {post.views}</span>
                          <span><FiHeart /> {post.likes}</span>
                          <span><FiMessageCircle /> {post.comments}</span>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </motion.div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.pagination}>
                  <button
                    className={styles.pageButton}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FiChevronLeft />
                  </button>
                  
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`${styles.pageButton} ${currentPage === i + 1 ? styles.active : ''}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  
                  <button
                    className={styles.pageButton}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    <FiChevronRight />
                  </button>
                </div>
              )}
            </main>
          </div>
        </div>
      </section>

      {/* Modal for Full Blog Content */}
      <AnimatePresence>
        {isModalOpen && selectedPost && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}>
            {/* Overlay */}
            <motion.div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
                zIndex: 999999,
                pointerEvents: 'auto'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            />
            
            {/* Modal Content */}
            <motion.div 
              style={{
                position: 'relative',
                width: '90%',
                maxWidth: '1000px',
                maxHeight: '90vh',
                backgroundColor: '#ffffff',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 30px 60px rgba(0, 0, 0, 0.5)',
                zIndex: 1000000,
                pointerEvents: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Close Button */}
              <button 
                onClick={closeModal}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.6)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  fontSize: '22px',
                  backdropFilter: 'blur(4px)',
                  WebkitBackdropFilter: 'blur(4px)'
                }}
                whileHover={{ 
                  background: 'linear-gradient(135deg, #B08B35, #AF8A32)',
                  rotate: 90,
                  scale: 1.1
                }}
              >
                <FiX />
              </button>

              {/* Scrollable Content */}
              <div style={{
                overflowY: 'auto',
                height: '100%'
              }}>
                {/* Header Image */}
                <div style={{
                  position: 'relative',
                  width: '100%',
                  height: '300px',
                  overflow: 'hidden'
                }}>
                  <img 
                    src={selectedPost.image} 
                    alt={selectedPost.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    background: 'linear-gradient(135deg, #B08B35, #AF8A32)',
                    color: '#ffffff',
                    padding: '8px 24px',
                    borderRadius: '40px',
                    fontSize: '14px',
                    fontWeight: '700',
                    zIndex: 5,
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {selectedPost.category}
                  </span>
                </div>

                {/* Content Body */}
                <div style={{
                  padding: '30px 40px 40px',
                  background: '#ffffff'
                }}>
                  <h1 style={{
                    fontSize: '2.2rem',
                    fontWeight: 800,
                    color: '#1a1a1a',
                    marginBottom: '20px',
                    lineHeight: 1.3
                  }}>
                    {selectedPost.title}
                  </h1>
                  
                  {/* Meta Info */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '25px',
                    paddingBottom: '20px',
                    borderBottom: '2px solid rgba(176, 139, 53, 0.15)',
                    flexWrap: 'wrap',
                    gap: '15px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #B08B35, #AF8A32)',
                        color: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '20px',
                        textTransform: 'uppercase'
                      }}>
                        {selectedPost.author.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{
                          fontWeight: 700,
                          color: '#1a1a1a',
                          fontSize: '16px',
                          marginBottom: '2px'
                        }}>
                          {selectedPost.author}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {selectedPost.authorRole}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      gap: '20px',
                      color: '#666',
                      fontSize: '14px',
                      background: '#f8f9fa',
                      padding: '10px 20px',
                      borderRadius: '40px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiCalendar color="#B08B35" /> {selectedPost.date}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiClock color="#B08B35" /> {selectedPost.readTime}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FiEye color="#B08B35" /> {selectedPost.views}
                      </span>
                    </div>
                  </div>

                  {/* Tags */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    marginBottom: '25px'
                  }}>
                    {selectedPost.tags.map(tag => (
                      <span key={tag} style={{
                        background: 'rgba(176, 139, 53, 0.1)',
                        color: '#B08B35',
                        padding: '6px 18px',
                        borderRadius: '30px',
                        fontSize: '13px',
                        fontWeight: 600,
                        border: '1px solid rgba(176, 139, 53, 0.2)'
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Excerpt */}
                  <div style={{
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    color: '#4a4a4a',
                    marginBottom: '30px',
                    padding: '25px',
                    background: 'rgba(176, 139, 53, 0.05)',
                    borderRadius: '16px',
                    borderLeft: '5px solid #B08B35',
                    fontStyle: 'italic'
                  }}>
                    {selectedPost.excerpt}
                  </div>

                  {/* Full Content */}
                  <div style={{
                    marginBottom: '35px'
                  }}>
                    {selectedPost.content.split('\n\n').map((paragraph, index) => (
                      <p key={index} style={{
                        fontSize: '1.1rem',
                        lineHeight: 1.8,
                        color: '#333',
                        marginBottom: '20px'
                      }}>
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Comments Section */}
                  {postComments[selectedPost.id] && postComments[selectedPost.id].length > 0 && (
                    <div style={{
                      marginBottom: '30px',
                      padding: '20px',
                      background: '#f8f9fa',
                      borderRadius: '16px'
                    }}>
                      <h4 style={{
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: '#1a1a1a',
                        marginBottom: '15px'
                      }}>
                        Comments ({postComments[selectedPost.id].length})
                      </h4>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px'
                      }}>
                        {postComments[selectedPost.id].map(comment => (
                          <div key={comment.id} style={{
                            padding: '15px',
                            background: '#ffffff',
                            borderRadius: '12px',
                            border: '1px solid rgba(176, 139, 53, 0.1)'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '8px'
                            }}>
                              <span style={{
                                fontWeight: 600,
                                color: '#B08B35'
                              }}>
                                {comment.author}
                              </span>
                              <span style={{
                                fontSize: '12px',
                                color: '#666'
                              }}>
                                {comment.date}
                              </span>
                            </div>
                            <p style={{
                              fontSize: '1rem',
                              lineHeight: 1.6,
                              color: '#333'
                            }}>
                              {comment.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comment Box */}
                  {showCommentBox && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      style={{
                        marginBottom: '30px',
                        padding: '20px',
                        background: '#f8f9fa',
                        borderRadius: '16px'
                      }}
                    >
                      <textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Write your comment..."
                        style={{
                          width: '100%',
                          padding: '15px',
                          border: '2px solid rgba(176, 139, 53, 0.2)',
                          borderRadius: '12px',
                          fontSize: '1rem',
                          marginBottom: '15px',
                          resize: 'vertical',
                          minHeight: '100px'
                        }}
                      />
                      <div style={{
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'flex-end'
                      }}>
                        <button
                          onClick={() => setShowCommentBox(false)}
                          style={{
                            padding: '10px 20px',
                            background: '#ffffff',
                            border: '2px solid rgba(176, 139, 53, 0.2)',
                            borderRadius: '30px',
                            color: '#666',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleCommentSubmit(selectedPost.id)}
                          style={{
                            padding: '10px 25px',
                            background: 'linear-gradient(135deg, #B08B35, #AF8A32)',
                            border: 'none',
                            borderRadius: '30px',
                            color: '#ffffff',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                        >
                          <FiSend /> Send
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '15px',
                    justifyContent: 'center',
                    paddingTop: '25px',
                    borderTop: '2px solid rgba(176, 139, 53, 0.15)',
                    flexWrap: 'wrap'
                  }}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleLike(selectedPost.id)}
                      style={{
                        padding: '12px 30px',
                        background: likedPosts[selectedPost.id] ? 'linear-gradient(135deg, #B08B35, #AF8A32)' : '#ffffff',
                        border: '2px solid rgba(176, 139, 53, 0.2)',
                        borderRadius: '40px',
                        color: likedPosts[selectedPost.id] ? '#ffffff' : '#4a4a4a',
                        fontWeight: 600,
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FiHeart color={likedPosts[selectedPost.id] ? '#ffffff' : '#B08B35'} />
                      {likedPosts[selectedPost.id] ? 'Liked' : 'Like'} ({selectedPost.likes})
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowCommentBox(!showCommentBox)}
                      style={{
                        padding: '12px 30px',
                        background: '#ffffff',
                        border: '2px solid rgba(176, 139, 53, 0.2)',
                        borderRadius: '40px',
                        color: '#4a4a4a',
                        fontWeight: 600,
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FiMessageCircle color="#B08B35" /> Comment ({selectedPost.comments})
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleSave(selectedPost.id)}
                      style={{
                        padding: '12px 30px',
                        background: savedPosts[selectedPost.id] ? 'linear-gradient(135deg, #B08B35, #AF8A32)' : '#ffffff',
                        border: '2px solid rgba(176, 139, 53, 0.2)',
                        borderRadius: '40px',
                        color: savedPosts[selectedPost.id] ? '#ffffff' : '#4a4a4a',
                        fontWeight: 600,
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <FiBookmark color={savedPosts[selectedPost.id] ? '#ffffff' : '#B08B35'} />
                      {savedPosts[selectedPost.id] ? 'Saved' : 'Save'}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Blog;